import type { VehicleCategoryDataVehicleCategoryEnum } from '~/api/prebooking'
import type { InitOverrideFunction } from '~/api/pricing-offers'
import type { ApiV2OffersPost200Response, Offer } from '~/api/pricing-offers/models'
import type { City } from '~/composables/city'
import { checkIsWebView } from '@camelot/webview-utils'
import * as Sentry from '@sentry/vue'
import { useDebounceFn } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { OffersApi } from '~/api/pricing-offers'
import { translate, type TranslationKey, useTranslations } from '~/composables/translate/translations'
import { useAppStore } from '~/stores/appstore'
import { useDateTimeStore } from '~/stores/datetime'
import { useExtrasStore } from '~/stores/extras'
import { useLocationStore } from '~/stores/location'
import { useReferralStore } from '~/stores/referral'
import { useStepStore } from '~/stores/step'
import { useVehiclesStore } from '~/stores/vehicles'

const offersApi = new OffersApi()

export const useOfferStore = defineStore('offer', () => {
  const possibleOffers = ref<Offer[]>([])
  const selectedOffer = ref<Offer | undefined>()
  const blurNotSelectedVehicles = ref<boolean>()
  const metaSelectedBuildSeries = ref<VehicleCategoryDataVehicleCategoryEnum | undefined>(undefined)

  const locationStore = useLocationStore()
  const vehiclesConfigurationStore = useVehiclesStore()
  const dateTimeStore = useDateTimeStore()
  const referralStore = useReferralStore()

  const startLoadingOffers = ref<boolean>(false)
  const loadingOffers = ref<boolean>(false)

  const getFormattedPricePerDay = computed<string | undefined>(() => selectedOffer.value
    ? formattedOfferPricePerDay(selectedOffer.value, useTranslations().language.value)
    : undefined,
  )

  const getCurrentBuildSeriesTranslation = computed<string | undefined>(() => {
    if (selectedOffer.value?.buildSeries === undefined) {
      return undefined
    }
    return translate(`pre_${selectedOffer.value.buildSeries.toUpperCase()}` as TranslationKey)
  })

  // TODO:
  //  here we set a timeout of 300ms to let the vehicle store loading new vehicle configuration
  //  otherwise we will load twice the offer (before and after the vehicle configuration has been loaded)
  //  and pricing won't be happy :D :D
  //  we need to have a loading boolean on the vehicle store to block this one also  + a mechanism to not trigger that directly when the start date changes
  const debouncedLoadPossibleOffers = useDebounceFn(loadPossibleOffers, 300)

  watchEffect(() => {
    let buildSeries: string[] = []
    if (referralStore.isMetaFlow && metaSelectedBuildSeries.value) {
      // For meta flow, use only the selected build series
      const selectedBuildSeries = vehiclesConfigurationStore.getBuildSeries(metaSelectedBuildSeries.value)
      if (selectedBuildSeries)
        buildSeries = [selectedBuildSeries]
    }
    else {
      // For non-meta flow, use all build series
      buildSeries = vehiclesConfigurationStore.getAllBuildSeries()
    }

    if (buildSeries.length === 0) {
      return // Exit the watchEffect if no build series are available
    }

    debouncedLoadPossibleOffers(
      useTranslations().language.value,
      locationStore.city,
      startLoadingOffers.value,
      locationStore.isValid && dateTimeStore.areDatesValid,
      buildSeries,
      dateTimeStore.startDate.date,
      dateTimeStore.endDate.date,
    )
      .catch((error) => {
        Sentry.captureMessage(error)
        console.error('Error while fetching the offers', error)
        startLoadingOffers.value = false
      })
      .finally(() => {
        loadingOffers.value = false
      })
  })

  async function loadPossibleOffers(
    language: string,
    city: City,
    startLoadingOffers: boolean,
    isValid: boolean,
    buildSeries: string[],
    startDate?: Date,
    endDate?: Date,
  ): Promise<void> {
    if (buildSeries.length === 0) {
      // console.debug('Vehicles configuration have not yet been loaded')
      return Promise.resolve()
    }

    if (startLoadingOffers && isValid) {
      loadingOffers.value = true

      const request = {
        buildSeries,
        pickupAt: startDate!,
        dropoffAt: endDate!,
        location: city.id,
      }

      let response: ApiV2OffersPost200Response

      if (useAppStore().accessToken !== '') {
        const token = useAppStore().accessToken
        const authenticatedRequest = {
          ...request,
          paymentProfileId: usePaymentProfileStore().selectedPaymentProfile?.paymentProfileId,
        }
        const authorizationOverride: InitOverrideFunction = async (context) => {
          return {
            ...context.init,
            headers: {
              ...context.init.headers,
              Authorization: `Bearer ${token}`,
            },
          }
        }
        response = await offersApi.apiV2OffersPost(
          {
            acceptLanguage: language,
            apiV2OffersPostRequest: authenticatedRequest,
          },
          authorizationOverride,
        )
      }
      else {
        const metaOffersEnabled = useRuntimeConfig().public.META_OFFERS_ENABLED
        const partner = referralStore.isMetaFlow && metaOffersEnabled ? 'meta' : undefined;

        response = await offersApi.apiV2OffersPost(
          {
            acceptLanguage: language,
            apiV2OffersPostRequest: request,
            partner: partner
          },
        )
      }

      possibleOffers.value = (response.offers ?? []).map(offer => ({ ...offer }))
      if (selectedOffer.value) {
        selectedOffer.value = possibleOffers.value
          .find((offer: Offer) => offer.carCategory === selectedOffer.value!.carCategory)
      }
    }
    else {
      // console.debug(`No offer loaded. buildSeries?.length: ${buildSeries?.length}, startLoadingOffers: ${startLoadingOffers}, startDate: ${startDate}, endDate: ${endDate}`)
      resetOffers()
      return Promise.resolve()
    }
  }

  function resetOffers() {
    possibleOffers.value = []
    if (selectedOffer.value) {
      selectedOffer.value = undefined
      useExtrasStore().reset()
      if (!checkIsWebView(window)) {
        useStepStore().reset()
      }
    }
  }

  const category = computed<VehicleCategoryDataVehicleCategoryEnum | undefined>(() =>
    selectedOffer.value?.buildSeries
      ? vehiclesConfigurationStore.getCategory(selectedOffer.value.buildSeries)
      : undefined,
  )

  const allBuildSeries = computed<string[]>(() =>
    selectedOffer.value?.buildSeries
      ? vehiclesConfigurationStore.getLinkedBuildSeries(selectedOffer.value.buildSeries)
      : [],
  )

  return {
    startLoadingOffers,
    loadingOffers,
    selectedOffer,
    possibleOffers,
    category,
    allBuildSeries,
    blurNotSelectedVehicles,
    getFormattedPricePerDay,
    getCurrentBuildSeriesTranslation,
    metaSelectedBuildSeries,
  }
})
